import { useState } from "react";
import { Play, Star, Users, Globe, ArrowRight, X } from 'lucide-react';

export default function Hero() {
  const [isPopupVisible, setIsPopupVisible] = useState(false);

  const togglePopup = () => {
    setIsPopupVisible(!isPopupVisible);
  };

  const stats = [
    { number: "50K+", label: "Active Members" },
    { number: "1000+", label: "Communities" },
    { number: "98%", label: "Satisfaction Rate" }
  ];

  return (
    <>
      <section className="relative min-h-screen flex items-center overflow-hidden dynamic-site-bg">
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-20 w-64 h-64 bg-purple-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-32 h-32 bg-blue-400 rounded-full blur-2xl"></div>
          <div className="absolute top-1/2 right-1/3 w-48 h-48 bg-indigo-400 rounded-full blur-2xl"></div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 lg:pt-32 pb-16">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-center">
            {/* Content */}
            <div className="text-center lg:col-span-3 lg:text-left text-white space-y-8">
              {/* Badge */}
              <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm rounded-full px-4 py-2 text-sm font-medium">
                <Globe className="w-4 h-4 text-blue-400" />
                Trusted by 1000+ Communities Worldwide
              </div>

              {/* Heading */}
              <div className="space-y-4">
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight">
                  Build Thriving
                  <span className="block bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                    Communities
                  </span>
                  That Connect & Grow
                </h1>
                
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/20">
                  <p className="text-xl font-semibold">Communiticate - Where Connections Flourish</p>
                </div>
              </div>

              {/* Description */}
              <p className="text-lg text-white/90 leading-relaxed max-w-2xl">
                Create, manage, and grow vibrant communities with powerful tools for engagement, networking, and collaboration. 
                Transform how people connect and build meaningful relationships.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={togglePopup}
                  className="group dynamic-site-bg-3 text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-2xl flex items-center justify-center gap-2"
                >
                  Start Your Community Free
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </button>
                
                <button className="group bg-white/20 backdrop-blur-sm hover:bg-white/30 text-white border border-white/30 px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2">
                  <Play className="w-5 h-5" />
                  Watch Demo
                </button>
              </div>

              {/* Stats */}
              <div className="grid grid-cols-3 gap-8 pt-8">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl sm:text-3xl font-bold text-white mb-1">
                      {stat.number}
                    </div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Illustration/Image */}
            <div className="flex justify-center lg:col-span-2 lg:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600 to-blue-600 rounded-3xl blur-2xl opacity-30 transform rotate-6"></div>
                <div className="relative z-10 bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                  {/* Community Preview */}
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h3 className="text-white font-semibold text-lg">Community Dashboard</h3>
                      <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                    </div>
                    
                    {/* Mock community cards */}
                    <div className="grid grid-cols-2 gap-4">
                      {[1, 2, 3, 4].map((i) => (
                        <div key={i} className="bg-white/20 rounded-lg p-4">
                          <div className="w-8 h-8 bg-gradient-to-r from-purple-400 to-blue-400 rounded-full mb-2"></div>
                          <div className="h-3 bg-white/30 rounded mb-1"></div>
                          <div className="h-2 bg-white/20 rounded w-3/4"></div>
                        </div>
                      ))}
                    </div>
                    
                    <div className="flex justify-center">
                      <button className="bg-purple-600 text-white px-6 py-2 rounded-lg text-sm font-medium">
                        Explore Communities
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Enhanced Popup */}
      {isPopupVisible && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden shadow-2xl">
            <div className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-semibold">Start Your Free Community</h3>
                <button
                  onClick={togglePopup}
                  className="p-2 rounded-full hover:bg-white/20 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-8">
              <form className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Community Name</label>
                  <input type="text" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="Enter your community name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                  <input type="email" className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600" placeholder="your@email.com" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Community Type</label>
                  <select className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-600">
                    <option>Professional Network</option>
                    <option>Hobby/Interest Group</option>
                    <option>Educational</option>
                    <option>Local Community</option>
                    <option>Business/Startup</option>
                  </select>
                </div>
                <button type="submit" className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-3 rounded-lg font-semibold hover:from-blue-600 hover:to-purple-600 transition-all duration-300">
                  Create Community
                </button>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
